import { FormattedMessage, useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef } from 'react'
import { useOnInView } from 'react-intersection-observer'
import {
  EmptyData,
  EmptyFolderText,
  RecursiveListItemContainer
} from '../../stories/organisms/top-navigation/styled-components.js'
import NotFoundComponent from '../../stories/molecules/not-found/index.jsx'
import { NotFound } from '../../stories/atoms/icons/core/index.js'
import RecursiveListNode from './recursive-list-node.jsx'
import { SpinnerGrey } from '../../stories/atoms/icons/special/index.js'
import { LoadContainer } from '../styled-components.js'
import { recursiveListTypeOptions } from '../constants.js'
import { useFolderTreeQuery } from '../utils.jsx'

const RecursiveListFetcher = ({
  folderId = null,
  projectId = null,
  search = '',
  selectedItem = null,
  setSelectedItem = () => {},
  listType,
  ...props
}) => {
  const { formatMessage } = useIntl()

  // project tree query
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useFolderTreeQuery({ listType, folderId, projectId, search })

  const allData = data ? data?.pages.flatMap((d) => d.content) : []

  const onBottomReached = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onBottomReachedRef = useRef(onBottomReached)

  const bottomReachedRef = useOnInView(
    useCallback((inView) => {
      if (inView) {
        onBottomReachedRef.current?.()
      }
    }, []),
    {
      rootMargin: '20px',
      skip: !hasNextPage
    }
  )

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  if (isLoading) {
    return (
      <LoadContainer>
        <SpinnerGrey />
      </LoadContainer>
    )
  }

  if (allData.length === 0) {
    if (folderId) {
      return (
        <EmptyFolderText>
          <FormattedMessage id="empty_folder" />
        </EmptyFolderText>
      )
    }

    return (
      <EmptyData>
        <NotFoundComponent
          icon={<NotFound style={{ width: '60px', height: '60px' }} />}
          header={formatMessage({ id: 'no_result_found' })}
          description={formatMessage({ id: 'no_result_found_description' })}
        />
      </EmptyData>
    )
  }

  return (
    <RecursiveListItemContainer {...props}>
      {allData.map((item, index) => {
        return (
          <RecursiveListNode
            item={item ?? null}
            search={search}
            key={item?.id ?? index}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            listType={listType}
            projectId={projectId}
          />
        )
      })}

      {isFetchingNextPage && (
        <LoadContainer>
          <SpinnerGrey />
        </LoadContainer>
      )}

      <div
        ref={bottomReachedRef}
        style={{
          height: '1px'
        }}
      />
    </RecursiveListItemContainer>
  )
}

RecursiveListFetcher.propTypes = {
  folderId: PropTypes.string,
  projectId: PropTypes.string,
  search: PropTypes.string,
  selectedItem: PropTypes.string,
  setSelectedItem: PropTypes.func,
  listType: PropTypes.oneOf(Object.values(recursiveListTypeOptions))
}

export default RecursiveListFetcher
